import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Calendar, ClockCounterClockwise, Warning, ArrowRight } from "@phosphor-icons/react";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

/**
 * Public candidate self-booking page — no auth. Rendered at `/book/{token}`.
 * Fetches available slots from `/api/public/book/{token}` and lets the
 * candidate confirm one slot. Backend records the booking + sends
 * confirmation emails to both sides.
 */
export default function PublicBookingPage() {
    const { token } = useParams();
    const [state, setState] = useState({ loading: true, error: "", data: null });
    const [picked, setPicked] = useState(null);
    const [confirming, setConfirming] = useState(false);
    const [booked, setBooked] = useState(null); // { booked_slot_at }

    const load = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/api/public/book/${token}`);
            if (r.data.status === "booked") {
                setBooked({ booked_slot_at: r.data.booked_slot_at, interviewer_name: r.data.interviewer_name });
                setState({ loading: false, error: "", data: r.data });
            } else {
                setState({ loading: false, error: "", data: r.data });
            }
        } catch (ex) {
            setState({
                loading: false,
                error: ex?.response?.data?.detail || "Unable to load booking details.",
                data: null,
            });
        }
    }, [token]);

    useEffect(() => { load(); }, [load]);

    const confirm = async () => {
        if (!picked || confirming) return;
        setConfirming(true);
        try {
            const r = await axios.post(`${API}/api/public/book/${token}/confirm`, { slot_at: picked });
            setBooked({ booked_slot_at: r.data.booked_slot_at, interviewer_name: state.data.interviewer_name, meet_link: r.data.meet_link });
        } catch (ex) {
            setState((s) => ({ ...s, error: ex?.response?.data?.detail || "Could not confirm slot." }));
        } finally {
            setConfirming(false);
        }
    };

    // Group slots by day for the grid
    const slotsByDay = {};
    for (const iso of state.data?.available_slots || []) {
        const d = new Date(iso);
        const dayKey = d.toLocaleDateString("en-IN", { weekday: "short", month: "short", day: "numeric" });
        (slotsByDay[dayKey] = slotsByDay[dayKey] || []).push(iso);
    }

    const formatSlot = (iso) => new Date(iso).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true });
    const formatFullSlot = (iso) => new Date(iso).toLocaleString("en-IN", {
        weekday: "long", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true,
    });

    return (
        <div className="min-h-screen bg-[#FAFAFA]" data-testid="public-booking-page">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
                {/* Brand header */}
                <div className="mb-8 pb-6 border-b border-[#E5E5E5]">
                    <p className="font-heading text-2xl font-black tracking-tighter">
                        naukri<span className="text-cyan-400">daddy</span>
                    </p>
                </div>

                {state.loading && (
                    <p className="text-sm text-[#525252]">Loading your booking details…</p>
                )}

                {state.error && !booked && (
                    <div className="border border-rose-200 bg-rose-50 p-6 flex items-start gap-3">
                        <Warning size={20} weight="bold" className="text-rose-600 shrink-0 mt-0.5" />
                        <div>
                            <p className="font-bold text-rose-900">Unable to book</p>
                            <p className="text-sm text-rose-800 mt-1">{state.error}</p>
                        </div>
                    </div>
                )}

                {booked && (
                    <div data-testid="booking-confirmed" className="border border-emerald-200 bg-emerald-50 p-8 text-center">
                        <div className="inline-flex items-center justify-center w-14 h-14 bg-emerald-100 border border-emerald-300 mb-4">
                            <CheckCircle size={28} weight="bold" className="text-emerald-700" />
                        </div>
                        <h1 className="font-heading text-3xl font-black text-emerald-900 mb-2">You&apos;re booked ✓</h1>
                        <p className="text-emerald-800 mb-6">A confirmation email is on its way.</p>
                        <div className="inline-block bg-white border border-emerald-200 px-6 py-4">
                            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-700 mb-1">Interview slot</p>
                            <p className="font-heading text-xl font-black text-ink">{formatFullSlot(booked.booked_slot_at)} IST</p>
                            {booked.interviewer_name && (
                                <p className="text-sm text-[#525252] mt-2">with <b>{booked.interviewer_name}</b></p>
                            )}
                        </div>
                        {booked.meet_link && (
                            <div className="mt-6">
                                <a
                                    href={booked.meet_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="join-meet-btn"
                                    className="inline-block bg-ink hover:bg-cobalt text-white px-6 py-3 text-xs font-black uppercase tracking-wider transition-colors"
                                >
                                    Join Google Meet →
                                </a>
                                <p className="text-[11px] text-[#525252] mt-2">The link is also in your confirmation email + calendar invite.</p>
                            </div>
                        )}
                    </div>
                )}

                {!booked && !state.loading && state.data && (
                    <>
                        <div className="mb-8">
                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                                Pick a slot
                            </p>
                            <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-ink mb-3">
                                {state.data.candidate_name ? `Hi ${state.data.candidate_name.split(" ")[0]},` : "Book your interview"}
                            </h1>
                            <p className="text-sm text-[#525252]">
                                {state.data.job_title && (
                                    <>Interview for <b>{state.data.job_title}</b>{state.data.client_name ? ` at ${state.data.client_name}` : ""}. </>
                                )}
                                Duration: <b>{state.data.duration_minutes} min</b>{state.data.interviewer_name ? <> · with <b>{state.data.interviewer_name}</b></> : null}.
                            </p>
                        </div>

                        {Object.keys(slotsByDay).length === 0 && (
                            <p className="text-sm text-[#525252]">No slots available in the next 7 days. Please contact your recruiter.</p>
                        )}

                        <div className="space-y-6 mb-8">
                            {Object.entries(slotsByDay).map(([day, slots]) => (
                                <div key={day} data-testid={`slot-day-${day.replace(/\s|,/g, "-")}`}>
                                    <p className="text-xs font-bold uppercase tracking-wider text-[#525252] mb-2 flex items-center gap-2">
                                        <Calendar size={12} weight="bold" /> {day}
                                    </p>
                                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                                        {slots.map((iso) => (
                                            <button
                                                key={iso}
                                                data-testid={`slot-btn-${iso}`}
                                                onClick={() => setPicked(iso)}
                                                className={`text-sm font-bold py-2 px-3 border transition-all ${
                                                    picked === iso
                                                        ? "bg-ink text-white border-ink"
                                                        : "bg-white border-[#E5E5E5] text-ink hover:border-ink"
                                                }`}
                                            >
                                                {formatSlot(iso)}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {picked && (
                            <div className="sticky bottom-4 bg-white border-2 border-ink shadow-xl p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                                <div className="flex-1">
                                    <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3]">You&apos;re about to book</p>
                                    <p className="font-heading text-lg font-black text-ink mt-0.5">{formatFullSlot(picked)}</p>
                                </div>
                                <button
                                    data-testid="confirm-booking-btn"
                                    onClick={confirm}
                                    disabled={confirming}
                                    className="btn-gradient px-6 py-3 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-40 shrink-0"
                                >
                                    {confirming ? "Confirming…" : "Confirm slot"} <ArrowRight size={12} weight="bold" />
                                </button>
                            </div>
                        )}

                        {state.data.expires_at && (
                            <p className="text-[10px] text-[#A3A3A3] mt-6 flex items-center gap-1">
                                <ClockCounterClockwise size={11} weight="bold" />
                                This link expires on {new Date(state.data.expires_at).toLocaleDateString("en-IN")}.
                            </p>
                        )}
                    </>
                )}

                {/* "Powered by NaukriDaddy" — organic growth chip */}
                <div className="mt-16 pt-6 border-t border-[#E5E5E5] text-center">
                    <a
                        href="https://naukridaddy.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="powered-by-chip"
                        className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3] hover:text-ink transition-colors"
                    >
                        Powered by <span className="font-heading font-black text-ink">naukri<span className="text-cyan-500">daddy</span></span>
                    </a>
                    <p className="text-[10px] text-[#A3A3A3] mt-2">The recruiter-first ATS built for Indian teams.</p>
                </div>
            </div>
        </div>
    );
}
